import React, { useState, useCallback } from 'react';
import styled, { useTheme, keyframes } from 'styled-components';
import {
  Button,
  Flex,
  Input,
  Loader,
  Select,
  Tag,
  Text,
  Textarea,
} from '../index';
import { HoneyPod } from '../Form/honeypod';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { companyPositions, startPrace, statusVisit } from '../Form/values';

type OptionProps = {
  maker?: boolean; //レセコンメーカー任意
  inquiry?: boolean; // お問い合わせ内容任意
  inquiryRequired?: boolean; // お問い合わせ内容必須
  opening?: boolean; // 開業状況
  visit?: boolean; // 訪問診療可否
  policy?: string; //独自プライバシーポリシー設定
  utmSource?: string; //参照元
  utmMedium?: string; //メディア識別
  utmCampaign?: string; //プロモーションコード
};

type Props = {
  url?: string;
  html?: React.ReactNode;
  id?: string;
  size?: 's' | 'm';
  children?: React.ReactNode;
  download?: boolean; //submitボタンの文言をダウンロードに
  position?: React.RefObject<any>; // サイドバナーの隠れる・表示されるスクロール量の計測
} & OptionProps;

const Root = styled.section<Props>`
  width: 100%;
  max-width: ${({ theme }) => theme.windowSizes.s};
  margin: 0 auto 80px;
  background-color: ${({ theme }) => theme.backgroundColors.white};
  box-shadow: ${({ theme }) => theme.boxShadow.base};
  border-radius: ${({ theme }) => theme.radii.l};
  max-height: calc(100vh - 100px);
  overflow: auto;

  ${({ theme }) =>
    theme.screen.sp({
      padding: `${theme.spaces.xxl} 0 0`,
    })}
`;

const FormList = styled(Flex)`
  width: 100%;
  padding: ${({ theme }) =>
    `${theme.spaces.m} ${theme.spaces.xxl} ${theme.spaces.xl}`};
  align-items: flex-start;
  flex-direction: column;
  gap: ${({ theme }) => theme.spaces.l};

  ${({ theme }) =>
    theme.screen.sp({
      padding: `${theme.spaces.xxl} ${theme.spaces.l} 0`,
    })}
`;

const FormItem = styled(Flex)`
  gap: ${({ theme }) => theme.spaces.m};
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
`;

const FormInput = styled(Flex)`
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  gap: ${({ theme }) => theme.spaces.s};
`;

const Label = styled.label`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.fontColors.lightgrey};
`;

const FormBottom = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: ${({ theme }) => theme.spaces.l};
`;

const Links = styled.a`
  display: inline-block;
  padding: ${({ theme }) => theme.spaces.xs} 0;
  transition-duration: ${({ theme }) => theme.transitions.normal};
  box-shadow: 0 1px 0 ${({ theme }) => theme.borderColors.black};

  &:hover {
    box-shadow: 0 1px 0 ${({ theme }) => theme.borderColors.default};
  }
`;

const Names = styled(Flex)`
  ${({ theme }) =>
    theme.screen.sp({
      flexDirection: 'column',
    })}
`;

const PolicyText = styled(Text)`
  a {
    display: inline-block;
    padding: ${({ theme }) => theme.spaces.xs} 0;
    transition-duration: ${({ theme }) => theme.transitions.normal};
    box-shadow: 0 1px 0 ${({ theme }) => theme.borderColors.black};

    &:hover {
      box-shadow: 0 1px 0 ${({ theme }) => theme.borderColors.default};
    }
  }
`;

const StepBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spaces.m};
`;

const StepList = styled.ul`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${({ theme }) => theme.spaces.l};
  margin: 0;
  padding: 0;

  &:after {
    position: absolute;
    z-index: -1;
    content: '';
    width: calc(100% - 64px);
    height: 2px;
    background-color: ${({ theme }) => theme.borderColors.default};
  }
`;
const StepItem = styled.li`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Motion = keyframes`
  0% {
    transform: scale(0.5);
    opacity: 1;
  }
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
`;

const StepCircle = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  background-color: ${({ theme }) => theme.borderColors.default};
  border-radius: ${({ theme }) => theme.radii.circle};

  &.active {
    width: 18px;
    height: 18px;
    background-color: ${({ theme }) => theme.backgroundColors.primary};
  }

  &.done {
    background-color: ${({ theme }) => theme.backgroundColors.lightgrey};
  }

  &.active:after {
    display: block;
    content: '';
    position: absolute;
    width: 12px;
    height: 12px;
    background-color: ${({ theme }) => theme.backgroundColors.primary};
    border-radius: ${({ theme }) => theme.radii.circle};
    animation: ${Motion} 2s ease-in-out infinite;
  }
`;

const BackButton = styled(Button)`
  padding: ${({ theme }) => theme.spaces.m};
  gap: ${({ theme }) => theme.spaces.s};
`;

const Back = styled.div`
  content: '';
  width: 10px;
  height: 10px;
  border-top: 2px solid ${({ theme }) => theme.borderColors.primary};
  border-right: 2px solid ${({ theme }) => theme.borderColors.primary};
  transform: rotate(-135deg);
`;

export const FormStep: React.FC<Props> = (props) => {
  const theme = useTheme();
  const [Submitting, setSubmitting] = useState(false);

  const HandleSubmit = useCallback((formData) => {
    setSubmitting(true);
    // console.log('Submitting data:', formData);
  }, []);

  // ステップ
  const [currentStep, setCurrentStep] = useState(1);

  // ステップ時のエラー判定
  const nextStep = async (validateForm, setTouched) => {
    const touchedFields = {
      familyName: true,
      givenName: true,
      companyName: currentStep === 2,
      companyPosition: currentStep === 2,
      tel: currentStep === 3,
      email: currentStep === 3,
      startPrace: currentStep === 4,
      statusVisit: currentStep === 4,
    };

    setTouched(touchedFields);
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep((prevStep) => prevStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const validationSchemas = [
    Yup.object().shape({
      familyName: Yup.string().required('姓を入力してください'),
      givenName: Yup.string().required('名を入力してください'),
    }),
    Yup.object().shape({
      companyName: Yup.string().required('医療機関名を入力してください'),
      companyPosition: Yup.string().required('役職を選択してください'),
    }),
    Yup.object().shape({
      tel: Yup.number()
        .required('携帯電話番号を入力してください')
        .typeError('半角数字を入力してください'),
      email: Yup.string()
        .email('メールアドレスの形式に誤りがあります。')
        .required('メールアドレスを入力してください'),
    }),
    Yup.object().shape({
      startPrace: Yup.string().required(
        '開業・分院開業の状況を入力してください',
      ),
      statusVisit:
        Yup.string().required('訪問診療の実施状況を入力してください'),
    }),
  ];

  return (
    <Root ref={props.position} id={props.id} size={props.size}>
      {props.html && props.html && props.html}
      <Formik
        initialValues={{
          familyName: '',
          givenName: '',
          companyName: '',
          companyPosition: '',
          tel: '',
          email: '',
          comment: '',
          startPrace: '',
          statusVisit: '',
        }}
        validationSchema={validationSchemas[currentStep - 1]}
        onSubmit={(values) => {}}
      >
        {({
          handleChange,
          handleBlur,
          values,
          errors,
          touched,
          validateForm,
          setTouched,
        }) => (
          <form action={props.url} method="post" onSubmit={HandleSubmit}>
            <FormList>
              <HoneyPod />

              <FormItem>
                <FormInput>
                  <Input
                    type="hidden"
                    name="utmSource"
                    placeholder="例）google"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={props.utmSource}
                    width={'100%'}
                  />
                  <Input
                    type="hidden"
                    name="utmMedium"
                    placeholder="例）mail"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={props.utmMedium}
                    width={'100%'}
                  />
                  <Input
                    type="hidden"
                    name="utmCampaign"
                    placeholder="例）20240528"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={props.utmCampaign}
                    width={'100%'}
                  />
                </FormInput>
              </FormItem>

              <StepList>
                {[1, 2, 3, 4].map((step) => (
                  <StepItem key={step}>
                    <StepCircle
                      className={
                        currentStep === step
                          ? 'active'
                          : currentStep > step
                          ? 'done'
                          : ''
                      }
                    />
                  </StepItem>
                ))}
              </StepList>

              <StepBox
                style={{
                  display: currentStep === 1 ? 'flex' : 'none',
                }}
              >
                <Names
                  width="100%"
                  justifyContent="space-between"
                  gap={theme.spaces.l}
                >
                  <FormItem>
                    <Label htmlFor="familyName">
                      姓
                      <Tag color="secondary" nowrap>
                        必須
                      </Tag>
                    </Label>
                    <FormInput>
                      <Input
                        type="text"
                        name="familyName"
                        placeholder="例）歯科"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.familyName}
                        error={touched.familyName && errors.familyName && true}
                        width={'100%'}
                        required
                      />
                      {touched.familyName && errors.familyName ? (
                        <Text size="s" color="pink" fontWeight="bold">
                          {errors.familyName}
                        </Text>
                      ) : null}
                    </FormInput>
                  </FormItem>

                  <FormItem>
                    <Label htmlFor="givenName">
                      名
                      <Tag color="secondary" nowrap>
                        必須
                      </Tag>
                    </Label>
                    <FormInput>
                      <Input
                        type="text"
                        name="givenName"
                        placeholder="例）太郎"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.givenName}
                        error={touched.givenName && errors.givenName && true}
                        width={'100%'}
                        required
                      />
                      {touched.givenName && errors.givenName ? (
                        <Text size="s" color="pink" fontWeight="bold">
                          {errors.givenName}
                        </Text>
                      ) : null}
                    </FormInput>
                  </FormItem>
                </Names>
              </StepBox>

              <StepBox
                style={{
                  display: currentStep === 2 ? 'flex' : 'none',
                }}
              >
                <FormItem>
                  <Label htmlFor="companyName">
                    医療機関名
                    <Tag color="secondary" nowrap>
                      必須
                    </Tag>
                  </Label>
                  <FormInput>
                    <Input
                      type="text"
                      name="companyName"
                      placeholder="例）メドレー歯科"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.companyName}
                      error={touched.companyName && errors.companyName && true}
                      width={'100%'}
                      required
                    />
                    {touched.companyName && errors.companyName ? (
                      <Text size="s" color="pink" fontWeight="bold">
                        {errors.companyName}
                      </Text>
                    ) : null}
                  </FormInput>
                </FormItem>

                <FormItem>
                  <Label htmlFor="companyPosition">
                    役職
                    <Tag color="secondary" nowrap>
                      必須
                    </Tag>
                  </Label>
                  <FormInput>
                    <Select
                      name="companyPosition"
                      value={values.companyPosition}
                      onChange={handleChange}
                      fill
                      required
                    >
                      {companyPositions.map((position) => (
                        <option key={position.value} value={position.value}>
                          {position.label}
                        </option>
                      ))}
                    </Select>
                    {touched.companyPosition && errors.companyPosition ? (
                      <Text size="s" color="pink" fontWeight="bold">
                        {errors.companyPosition}
                      </Text>
                    ) : null}
                  </FormInput>
                </FormItem>
              </StepBox>

              <StepBox
                style={{
                  display: currentStep === 3 ? 'flex' : 'none',
                }}
              >
                <FormItem>
                  <Label htmlFor="tel">
                    携帯電話番号
                    <Tag color="secondary" nowrap>
                      必須
                    </Tag>
                  </Label>
                  <FormInput>
                    <Input
                      type="tel"
                      name="tel"
                      placeholder="例）08012345678"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.tel}
                      error={touched.tel && errors.tel && true}
                      width={'100%'}
                      required
                    />
                    {touched.tel && errors.tel ? (
                      <Text size="s" color="pink" fontWeight="bold">
                        {errors.tel}
                      </Text>
                    ) : null}
                  </FormInput>
                </FormItem>

                <FormItem>
                  <Label htmlFor="email">
                    メールアドレス
                    <Tag color="secondary" nowrap>
                      必須
                    </Tag>
                  </Label>
                  <FormInput>
                    <Input
                      type="email"
                      name="email"
                      placeholder="例）info@dentis-cloud.com"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      error={touched.email && errors.email && true}
                      width={'100%'}
                      required
                    />
                    {touched.email && errors.email ? (
                      <Text size="s" color="pink" fontWeight="bold">
                        {errors.email}
                      </Text>
                    ) : null}
                  </FormInput>
                </FormItem>
              </StepBox>

              <StepBox style={{ display: currentStep === 4 ? 'flex' : 'none' }}>
                {props.opening && (
                  <FormItem>
                    <Label htmlFor="startPrace">
                      開業・分院開業の状況
                      <Tag color="secondary" nowrap>
                        必須
                      </Tag>
                    </Label>

                    <Select
                      name="startPrace"
                      value={values.startPrace}
                      onChange={handleChange}
                      fill
                      required
                    >
                      {startPrace.map((position) => (
                        <option key={position.value} value={position.value}>
                          {position.label}
                        </option>
                      ))}
                    </Select>
                  </FormItem>
                )}

                {props.visit && (
                  <FormItem>
                    <Label htmlFor="statusVisit">
                      訪問診療の実施状況
                      <Tag color="secondary" nowrap>
                        必須
                      </Tag>
                    </Label>

                    <Select
                      name="statusVisit"
                      value={values.statusVisit}
                      onChange={handleChange}
                      fill
                      required
                    >
                      {statusVisit.map((position) => (
                        <option key={position.value} value={position.value}>
                          {position.label}
                        </option>
                      ))}
                    </Select>
                  </FormItem>
                )}

                {props.maker && (
                  <FormItem>
                    <Label htmlFor="receiptComputer">
                      お使いのレセコンメーカー
                      <Tag color="default" nowrap>
                        任意
                      </Tag>
                    </Label>
                    <FormInput>
                      <Input
                        type="text"
                        name="receiptComputer"
                        width={'100%'}
                      />
                    </FormInput>
                  </FormItem>
                )}

                {props.inquiry && (
                  <FormItem>
                    <Label htmlFor="comment">
                      お問い合わせ内容
                      <Tag color="default" nowrap>
                        任意
                      </Tag>
                    </Label>
                    <FormInput>
                      <Textarea
                        name="comment"
                        placeholder="製品に関する機能や価格、導入のご検討などお気軽にお問い合わせください"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.comment}
                        rows={5}
                        width={'100%'}
                      />
                    </FormInput>
                  </FormItem>
                )}

                {props.inquiryRequired && (
                  <FormItem>
                    <Label htmlFor="comment">
                      お問い合わせ内容
                      <Tag color="secondary" nowrap>
                        必須
                      </Tag>
                    </Label>
                    <FormInput>
                      <Textarea
                        name="comment"
                        placeholder="製品に関する機能や価格、導入のご検討などお気軽にお問い合わせください"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.comment}
                        rows={5}
                        width={'100%'}
                        required
                      />
                    </FormInput>
                  </FormItem>
                )}
              </StepBox>

              <Flex justifyContent="space-between" width="100%">
                {currentStep > 1 && currentStep < 4 && (
                  <BackButton color="transparent" onClick={prevStep}>
                    <Back />
                    戻る
                  </BackButton>
                )}
                {currentStep < 4 && (
                  <Button
                    type="button"
                    ml="auto"
                    onClick={() => nextStep(validateForm, setTouched)}
                  >
                    次へ
                  </Button>
                )}
                {currentStep === 4 && (
                  <FormBottom>
                    <Flex alignItems="flex-start" width="100%">
                      {props.policy ? (
                        <PolicyText
                          size={props.size === 's' ? 'xs' : 's'}
                          lineHeight="m"
                          dangerouslySetInnerHTML={{ __html: props.policy }}
                        />
                      ) : (
                        <Text as="p" size={props.size === 's' ? 'xs' : 's'}>
                          「
                          <Links
                            href="https://clinics-cloud.com/privacy"
                            target="_blank"
                          >
                            プライバシーポリシー
                          </Links>
                          」 に同意いただいた上で、
                          {props.download ? 'ダウンロード' : '送信'}
                          ボタンを押してください
                        </Text>
                      )}
                    </Flex>

                    <Flex
                      flexDirection="column"
                      alignItems="flex-start"
                      gap={theme.spaces.l}
                      width="100%"
                    >
                      <Button
                        type="submit"
                        size={props.size === 'm' ? 'l' : 'm'}
                        wide="l"
                        fill
                        radius="rounded"
                        disabled={Submitting ? true : false}
                      >
                        {Submitting ? (
                          <Flex alignItems="center">
                            <Loader mr={theme.spaces.m} />
                            送信中
                          </Flex>
                        ) : props.download ? (
                          'ダウンロード(無料)'
                        ) : (
                          '送信'
                        )}
                      </Button>
                      <BackButton color="transparent" onClick={prevStep}>
                        <Back />
                        戻る
                      </BackButton>
                    </Flex>
                  </FormBottom>
                )}
              </Flex>
            </FormList>
          </form>
        )}
      </Formik>
    </Root>
  );
};
